import React from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Mail, Lock, User, Eye, EyeOff, ArrowLeft, Pencil, Check, Rocket } from 'lucide-react';
import { SharkAvatarSelector, SHARK_COLORS, SHARK_SPECIES } from '../../../admin/components/SharkAvatarSelector';
import { Input } from '@/shared/components/atoms/Input';
import { Button } from '@/shared/components/atoms/Button';
import { AuthLayout } from '../../components/AuthLayout';
import { useSignupHook } from './useSignupHook';
import './Signup.css';

const FEATURES_STEP1 = [
  {
    title: '2 Free Calls',
    body: 'Get started immediately - no credit card required. Experience real-time intelligence firsthand.',
  },
  {
    title: 'Live Response Co-Pilot',
    body: 'Direct reframes and objection anchors generated instantly as prospects speak.',
  },
  {
    title: 'Upgrade Anytime',
    body: "Unlock unlimited calls and full CRM sync when you're ready to scale.",
  },
];

const slideVariants = {
  hidden: { opacity: 0, x: 20 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.35, ease: 'easeOut' as const } },
  exit: { opacity: 0, x: -16, transition: { duration: 0.25 } },
};

const SignupLeftContent = ({ step }: { step: number }) => (
  <>
    <p className="signup-tagline">
      {step === 1
        ? 'Your autonomous sales co-pilot. Built to brief you, guide you, and sync your workflow instantly.'
        : "Pick your co-pilot. Give it a name. It's quick, it's fun - and then you're in."}
    </p>

    <div className="signup-features">
      {FEATURES_STEP1.map((f, i) => (
        <div
          key={f.title}
          className="signup-feature-item"
          style={{ opacity: step >= (i === 2 ? 2 : 1) ? 1 : 0.4 }}
        >
          <span className="login-feature-bullet" />
          <p className="login-feature-text">
            <strong>{f.title}:</strong> {f.body}
          </p>
        </div>
      ))}
    </div>
  </>
);

export function Signup() {
  const {
    formData,
    step,
    showPassword,
    isLoading,
    shark,
    setStep,
    setShowPassword,
    handleChange,
    handleNext,
    submitForm,
    handleSharkSpeciesChange,
    handleSharkCostumeChange,
    handleSharkNameChange,
    handleSharkColorChange
  } = useSignupHook();

  return (
    <AuthLayout leftContent={<SignupLeftContent step={step} />}>
      {/* Step indicator */}
      <div className="signup-step-indicator">
        {[1, 2].map((s) => (
          <div
            key={s}
            className={`signup-step-circle ${s <= step ? 'active' : 'inactive'}`}
          >
            {s < step ? <Check size={11} strokeWidth={3} /> : s}
          </div>
        ))}
        <span className="signup-step-text">
          Step {step} of 2 - {step === 1 ? 'Create Account' : 'Meet Your Shark'}
        </span>
      </div>

      <div style={{ width: '100%', maxWidth: '448px', position: 'relative' }}>
        <AnimatePresence mode="wait">
          {step === 1 && (
            <motion.div key="step1" variants={slideVariants} initial="hidden" animate="visible" exit="exit">
              <h2 className="auth-heading">Create Account</h2>
              <p className="auth-subheading">
                Let's get you set up with your autonomous sales co-pilot.
              </p>

              <div className="auth-input-group" style={{ marginBottom: '12px' }}>
                <Input
                  label="Full Name"
                  leftIcon={<User size={13} />}
                  type="text"
                  name="name"
                  id="signup-name"
                  placeholder="Arthur Pendragon"
                  value={formData.name}
                  onChange={handleChange}
                  autoComplete="name"
                  required
                />
                <Input
                  label="Email Address"
                  leftIcon={<Mail size={13} />}
                  type="email"
                  name="email"
                  id="signup-email"
                  placeholder="you@company.com"
                  value={formData.email}
                  onChange={handleChange}
                  autoComplete="email"
                  required
                />
                <Input
                  label="Password"
                  leftIcon={<Lock size={13} />}
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  id="signup-password"
                  placeholder="••••••••"
                  value={formData.password}
                  onChange={handleChange}
                  autoComplete="new-password"
                  required
                  rightIcon={
                    <button
                      type="button"
                      onClick={() => setShowPassword((v) => !v)}
                      className="password-toggle-btn"
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                    >
                      {showPassword ? <EyeOff size={15} /> : <Eye size={15} />}
                    </button>
                  }
                />
              </div>

              <Button
                type="button"
                variant="secondary"
                fullWidth
                onClick={handleNext}
                rightIcon={<span style={{ fontSize: '14px', marginLeft: '2px' }}>→</span>}
              >
                Continue
              </Button>

              <p className="auth-switch-link-text">
                Already have an account?{' '}
                <Link to="/login" className="auth-switch-link">
                  Login here
                </Link>
              </p>
            </motion.div>
          )}

          {step === 2 && (
            <motion.div key="step2" variants={slideVariants} initial="hidden" animate="visible" exit="exit">
              <h2 className="auth-heading">Meet Your Shark</h2>
              <p className="auth-subheading">
                Pick your co-pilot and give it a name. This is the AI that's got your back on every call.
              </p>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <SharkAvatarSelector
                  selectedSpecies={shark.species || 'blue_shark'}
                  sharkColor={shark.color}
                  sharkCostume={shark.costume}
                  onSpeciesChange={handleSharkSpeciesChange}
                  onCostumeChange={handleSharkCostumeChange}
                  variant="signup"
                >
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    <Input
                      label="Name your Shark"
                      leftIcon={<Pencil size={12} />}
                      type="text"
                      placeholder={SHARK_SPECIES.find(s => s.id === (shark.species || 'blue_shark'))?.defaultName || 'Finn'}
                      value={shark.name}
                      onChange={(e) => handleSharkNameChange(e.target.value)}
                      required
                    />
                  </div>

                  <div style={{ display: 'none' }}>
                    {SHARK_COLORS.map((color) => {
                      const isSelected = shark.color === color.hex;
                      return (
                        <div
                          key={color.id}
                          onClick={() => handleSharkColorChange(color.hex)}
                          title={color.name}
                          style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                        >
                          <motion.div
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.95 }}
                            style={{
                              width: '26px',
                              height: '26px',
                              borderRadius: '50%',
                              background: color.hex === 'transparent' ? 'rgba(255,255,255,0.08)' : color.hex,
                              border: isSelected ? `2px solid #00cfcf` : '1px solid rgba(255,255,255,0.12)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            {isSelected && <Check size={12} color="#fff" strokeWidth={3} />}
                          </motion.div>
                        </div>
                      );
                    })}
                  </div>
                </SharkAvatarSelector>

                <Button
                  type="button"
                  variant="secondary"
                  fullWidth
                  isLoading={isLoading}
                  onClick={submitForm}
                  style={{ marginTop: '8px' }}
                  rightIcon={<Rocket size={14} />}
                >
                  {isLoading ? '' : `Let's go, ${shark.name}!`}
                </Button>

                <Button
                  type="button"
                  variant="ghost"
                  fullWidth
                  onClick={() => setStep(1)}
                  leftIcon={<ArrowLeft size={13} />}
                >
                  Back
                </Button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </AuthLayout>
  );
}
